import React, { useState } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  Button,
  Divider,
  Badge,
  Tooltip,
  Fab,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Assignment as AssignmentIcon,
  Event as EventIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';

const CompactTasksEventsDrawer = ({
  open,
  onClose,
  onToggle,
  calendarData,
  TaskBox,
  EventBox,
  handleDuplicateTask,
  handleDuplicateEvent,
  setTaskModalOpen,
  setEventModalOpen,
}) => {
  const unscheduledTasks = calendarData['unscheduled-tasks']?.tasks || [];
  const unscheduledEvents = calendarData['unscheduled-events']?.events || [];

  const totalItems = unscheduledTasks.length + unscheduledEvents.length;

  return (
    <>
      {/* Floating Toggle Button (when drawer is closed) */}
      {!open && (
        <Tooltip title="Aufgaben & Ereignisse">
          <Fab
            color="secondary"
            sx={{
              position: 'fixed',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1200,
            }}
            onClick={onToggle}
          >
            <Badge badgeContent={totalItems} color="error">
              <AssignmentIcon />
            </Badge>
          </Fab>
        </Tooltip>
      )}

      {/* Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 400 },
            bgcolor: 'background.default',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignmentIcon />
              <Typography variant="h6" fontWeight={600}>
                Aufgaben & Ereignisse
              </Typography>
            </Box>
            <IconButton onClick={onClose} sx={{ color: 'inherit' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ p: 2, display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              fullWidth
              onClick={() => {
                setTaskModalOpen(true);
                onClose();
              }}
            >
              Neue Aufgabe
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<AddIcon />}
              fullWidth
              onClick={() => {
                setEventModalOpen(true);
                onClose();
              }}
            >
              Neue Veranstaltung
            </Button>
          </Box>

          <Divider />

          {/* Content */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {/* Tasks Section */}
            {unscheduledTasks.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600} color="primary">
                    Aufgaben
                  </Typography>
                  <Chip
                    label={unscheduledTasks.length}
                    size="small"
                    color="primary"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {unscheduledTasks.map((task) => (
                    <Box
                      key={task.id}
                      sx={{
                        p: 1.5,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 1,
                        },
                      }}
                    >
                      <TaskBox
                        task={task}
                        onDuplicate={() => handleDuplicateTask(task)}
                        source="unscheduled-tasks"
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Events Section */}
            {unscheduledEvents.length > 0 && (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600} color="secondary">
                    Ereignisse
                  </Typography>
                  <Chip
                    label={unscheduledEvents.length}
                    size="small"
                    color="secondary"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {unscheduledEvents.map((event) => (
                    <Box
                      key={event.id}
                      sx={{
                        p: 1.5,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          borderColor: 'secondary.main',
                          boxShadow: 1,
                        },
                      }}
                    >
                      <EventBox
                        event={event}
                        onDuplicate={() => handleDuplicateEvent(event)}
                        source="unscheduled-events"
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Empty State */}
            {unscheduledTasks.length === 0 && unscheduledEvents.length === 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  gap: 2,
                  color: 'text.secondary',
                }}
              >
                <EventIcon sx={{ fontSize: 64, opacity: 0.3 }} />
                <Typography variant="body1" textAlign="center">
                  Keine außerplanmäßigen Artikel
                </Typography>
                <Typography variant="body2" textAlign="center" color="text.disabled">
                  Erstellen Sie neue Aufgaben oder Ereignisse mit den Schaltflächen oben
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default CompactTasksEventsDrawer;
